/**
 * Settings > ReviewHub (ADR-0023). Company admins are ReviewHub admins.
 *   ReviewHub        the on/off switch per company (platform admins), who uses it, the self-check
 *                    switch, the fiscal year, and which TechHub services carry an Assurance mark
 *   Rating definitions   the thresholds behind every color (seeded with ITAssurance's)
 *   Reminders and alerts each step of the cycle and who hears about it
 *   Client page          which sections every review's page has, and their order
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, type AdminTenant, type Member, type RhSettingsResp } from "../../lib/api";
import { useStore } from "../../lib/store";
import { byCoOrder, coName } from "../../lib/brand";
import { Loading, SaveBar, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";
import { AREAS, REMINDERS, ruleText, type MetricDef, type Rule } from "../../../../shared/reviewhub";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** The companies where I can manage ReviewHub, with ?co= to pick. */
function useCompany() {
  const { p } = useStore();
  const [sp, setSp] = useSearchParams();
  const codes = p.rhCodes.filter((c) => p.rh(c).admin);
  const co = codes.includes(sp.get("co") ?? "") ? sp.get("co")! : codes[0] ?? "";
  return { codes, co, pick: (c: string) => setSp({ co: c }, { replace: true }) };
}
function CompanyTabs({ codes, co, pick }: { codes: string[]; co: string; pick: (c: string) => void }) {
  if (codes.length < 2) return null;
  return <div className="ctabs">{codes.map((c) => <button key={c} className={`ctab ${c === co ? "on" : ""}`} onClick={() => pick(c)}><Tile code={c} /><span>{coName(c)}</span></button>)}</div>;
}
function useSettings(co: string) {
  const { toast } = useStore();
  const [s, setS] = useState<RhSettingsResp | null>(null);
  const load = () => (co ? api.rhSettings(co).then(setS, (e: Error) => toast(e.message)) : Promise.resolve());
  useEffect(() => { setS(null); void load(); }, [co]);
  const save = async (b: Record<string, unknown>, ok = "Saved.") => { try { await api.rhSaveSettings(co, b); toast(ok); await load(); return true; } catch (e) { toast((e as Error).message); return false; } };
  return { s, save };
}

// ---------------------------------------------------------------- ReviewHub (access and general)
export function ReviewHubAccessPage() {
  const { p, me, toast, reload } = useStore();
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [err, setErr] = useState<string | null>(null);
  const { codes, co, pick } = useCompany();
  const { s, save } = useSettings(co);
  const load = async () => {
    try {
      const ts = (await api.adminTenants()).tenants.sort(byCoOrder);
      setTenants(ts);
      const on = ts.filter((t) => (t.modules ?? []).includes("reviewhub"));
      const lists = await Promise.all(on.map((t) => api.members(t.code).then((r) => [t.code, r.members] as const, () => [t.code, []] as const)));
      setMembers(Object.fromEntries(lists));
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);
  if (!p.anyAdmin) return <Navigate to="/settings" replace />;
  if (err) return <div className="note bad">{err}</div>;
  if (!tenants) return <Loading />;
  const pa = me.user.is_platform_admin;
  const flip = async (t: AdminTenant, on: boolean) => {
    if (!on && !window.confirm(`Switch ReviewHub off for ${t.name}? It disappears for everyone there; nothing is deleted, and switching it back on brings everything back.`)) return;
    try {
      await api.setModules(t.code, { reviewhub: on });
      toast(on ? `ReviewHub is on for ${t.name}. Give people ReviewHub roles in Settings > Team.` : `ReviewHub is off for ${t.name}.`);
      await load(); await reload();
    } catch (e) { toast((e as Error).message); }
  };
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="reviewhub" extra="Add-on to the Vault" />
        <h1 className="title">ReviewHub</h1>
        <div className="lede">Quarterly technology business reviews on your TechHub clients: ratings from evidence, risks, a roadmap that isn't a pitch, commitments, and the client page. It needs TechHub on for the same company.</div>
      </div></div>
      <div className="addons">
        {tenants.map((t) => {
          const on = (t.modules ?? []).includes("reviewhub");
          const hub = (t.modules ?? []).includes("clienthub");
          const ms = members[t.code] ?? [];
          const n = (r: string) => ms.filter((m) => m.roles.includes(r as Member["roles"][number])).length;
          return (
            <section key={t.code} className="card addon">
              <div className="hd">
                <Tile code={t.code} />
                <div><b>{t.name}</b><div className="cap">{on ? (hub ? "On" : "On, but TechHub is off") : "Off"}</div></div>
                <label className="switch" style={{ marginLeft: "auto" }} title={pa ? "" : "Only platform admins switch add-ons"}>
                  <input type="checkbox" checked={on} disabled={!pa} onChange={(e) => void flip(t, e.target.checked)} aria-label={`ReviewHub for ${t.name}`} /><span />
                </label>
              </div>
              {on ? (<>
                <div className="row"><span>ReviewHub viewers</span><b>{n("reviewhub_viewer")}</b></div>
                <div className="row"><span>ReviewHub editors</span><b>{n("reviewhub_editor")}</b></div>
                <div className="row"><span>Admins (also ReviewHub admins)</span><b>{n("admin")}</b></div>
                <div className="row"><Link className="doclink" to={`/settings/people?co=${t.code}`}>Manage who can use it</Link><span /></div>
              </>) : <div className="cap">Turning it on adds ReviewHub to the part switch for the people you give ReviewHub roles. Turning it off hides it; nothing is deleted.</div>}
            </section>
          );
        })}
      </div>
      {codes.length > 0 && <>
        <h2 className="rh-h2">How reviews work at {coName(co)}</h2>
        <CompanyTabs codes={codes} co={co} pick={pick} />
        {!s ? <Loading /> : (
          <div className="card sect">
            <div className="setrow">
              <div className="tx"><b>Allow self-check when no one else is available</b><span>The Pitch check is normally a colleague who didn't build the review. With this on, the owner can check their own review; it shows as Self-checked.</span></div>
              <label className="switch"><input type="checkbox" checked={s.settings.allow_self_check} disabled={!s.can_manage} onChange={(e) => void save({ allow_self_check: e.target.checked })} aria-label="Allow self-check" /><span /></label>
            </div>
            <div className="setrow">
              <div className="tx"><b>Fiscal year starts in</b><span>Names the quarters: with October, FY27-Q1 is October to December 2026.</span></div>
              <select className="inp" style={{ width: 180, flex: "none" }} value={s.settings.fy_start_month} disabled={!s.can_manage} onChange={(e) => void save({ fy_start_month: Number(e.target.value) })}>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
            </div>
            <div className="setrow" style={{ alignItems: "flex-start" }}>
              <div className="tx"><b>Service-line marks on the client page</b><span>{s.marks_available.length ? "A mark shows only for a line the client buys (their Services in TechHub). Pick which mark each service carries." : "These are ITAssurance's own brand marks, so they only show on ITAssurance client pages."}</span></div>
              {s.marks_available.length > 0 && <div className="rh-marks">
                {s.services.map((svc) => (
                  <label key={svc} className="rh-mk"><span>{svc}</span>
                    <select className="inp inp-sm" value={s.settings.service_marks[svc] ?? ""} disabled={!s.can_manage}
                      onChange={(e) => void save({ service_marks: { ...s.settings.service_marks, [svc]: e.target.value } })}>
                      <option value="">No mark</option>{s.marks_available.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
                ))}
                {!s.services.length && <span className="cap">No TechHub services yet.</span>}
              </div>}
            </div>
          </div>
        )}
      </>}
      <div className="foot-note">Only platform admins switch add-ons on or off. Company admins are ReviewHub admins: they set the thresholds, reminders and client page sections in this menu.</div>
    </div>
  );
}

// ---------------------------------------------------------------- Rating definitions
type Level = "green" | "yellow" | "red";
const LEVEL_WORD: Record<Level, string> = { green: "Healthy", yellow: "Attention", red: "Action required" };
const unitOf = (d: MetricDef) => (d.kind === "pct" ? "%" : d.kind === "days" ? " days" : d.kind === "months" ? " months" : "");
const isNum = (d: MetricDef) => d.kind !== "bool" && d.kind !== "enum";
/** Higher is better when the rules fire below a number (SLA %), lower is better when they fire above one (open gaps). */
const higherBetter = (d: MetricDef) => d.rules.some((r) => r.op === "<" || r.op === "<=");

/** One colored band per level, best first. `edit` = index of the rule whose number is typed in this band. */
interface Band { level: Level; parts: (string | { rule: number })[] }
function bandsOf(d: MetricDef): Band[] {
  const u = unitOf(d);
  const ri = d.rules.findIndex((r) => r.level === "red");
  const yi = d.rules.findIndex((r) => r.level === "yellow");
  if (!isNum(d)) {
    const words = (lvl: Level) => {
      if (d.kind === "bool") {
        const hit = d.rules.filter((r) => r.level === lvl).map((r) => (r.v ? d.yes ?? "Yes" : d.no ?? "No"));
        if (lvl !== "green") return hit;
        const used = new Set(d.rules.map((r) => r.v));
        return [true, false].filter((v) => !used.has(v)).map((v) => (v ? d.yes ?? "Yes" : d.no ?? "No"));
      }
      const used = new Set(d.rules.map((r) => r.v));
      const opts = d.options ?? [];
      return lvl === "green" ? opts.filter((o) => !used.has(o.value)).map((o) => o.label) : opts.filter((o) => d.rules.some((r) => r.level === lvl && r.v === o.value)).map((o) => o.label);
    };
    return (["green", "yellow", "red"] as Level[]).map((l) => ({ level: l, parts: [words(l).join(" or ")] })).filter((b) => b.parts[0]);
  }
  const hb = higherBetter(d);
  const n = (i: number) => ({ rule: i });
  const out: Band[] = [];
  // the boundary between green and the first warning level
  const first = yi >= 0 ? yi : ri;
  const fop = d.rules[first]!.op;
  if (hb) {
    out.push({ level: "green", parts: fop === "<" ? [`{${first}}`, `${u} and up`] : ["Over ", `{${first}}`, u] });
    if (yi >= 0 && ri >= 0) out.push({ level: "yellow", parts: [`{${ri}}`, `${u} to under `, n(yi), u] });
    else if (yi >= 0) out.push({ level: "yellow", parts: ["Under ", n(yi), u] });
    if (ri >= 0) out.push({ level: "red", parts: ["Under ", n(ri), u] });
  } else {
    out.push({ level: "green", parts: fop === ">" ? [`{${first}}`, `${u} or less`] : ["Under ", `{${first}}`, u] });
    if (yi >= 0 && ri >= 0) out.push({ level: "yellow", parts: fop === ">" ? ["Over ", n(yi), ", up to ", `{${ri}}`, u] : [n(yi), `${u} to `, `{${ri}}`, u] });
    else if (yi >= 0) out.push({ level: "yellow", parts: fop === ">" ? ["Over ", n(yi), u] : [n(yi), `${u} or more`] });
    if (ri >= 0) out.push({ level: "red", parts: d.rules[ri]!.op === ">" ? ["Over ", n(ri), u] : [n(ri), `${u} or more`] });
  }
  // green band of "red above 0" counts reads better as just "0"
  if (!hb && d.rules.length === 1 && Number(d.rules[0]!.v) === 0 && fop === ">") out[0] = { level: "green", parts: ["None"] };
  return out;
}

function orderProblem(d: MetricDef, vals: number[]): string | null {
  const ri = d.rules.findIndex((r) => r.level === "red");
  const yi = d.rules.findIndex((r) => r.level === "yellow");
  if (vals.some((v) => !Number.isFinite(v) || v < 0)) return "Enter a number.";
  if (d.kind === "pct" && vals.some((v) => v > 100)) return "A percentage is 0 to 100.";
  if (ri < 0 || yi < 0) return null;
  if (higherBetter(d) ? vals[ri]! >= vals[yi]! : vals[ri]! <= vals[yi]!) return higherBetter(d) ? "The red line has to be below where Attention starts." : "The red line has to be above where Attention starts.";
  return null;
}

export function RatingDefinitionsPage() {
  const { codes, co, pick } = useCompany();
  const { s, save } = useSettings(co);
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [area, setArea] = useState<string>(AREAS[0]!.key);
  const [busy, setBusy] = useState(false);
  if (!codes.length) return <Navigate to="/settings" replace />;
  if (!s) return <Loading />;
  const defs = s.metrics as unknown as MetricDef[];
  const saved = (d: MetricDef): number[] => ((s.settings.rule_overrides[d.key] as Rule[] | undefined) ?? d.rules).map((r) => Number(r.v));
  const current = (d: MetricDef): string[] => draft[d.key] ?? saved(d).map(String);
  const standard = (d: MetricDef) => d.rules.map((r) => Number(r.v));
  const differs = (d: MetricDef) => isNum(d) && current(d).some((v, i) => Number(v) !== standard(d)[i]);
  const problems = defs.map((d) => (draft[d.key] ? orderProblem(d, draft[d.key]!.map(Number)) : null)).filter(Boolean);
  const set = (d: MetricDef, i: number, v: string) => {
    const next = current(d).map((x, n) => (n === i ? v : x));
    setDraft((dr) => { const o = { ...dr }; if (next.every((x, n) => Number(x) === saved(d)[n])) delete o[d.key]; else o[d.key] = next; return o; });
  };
  const reset = (d: MetricDef) => setDraft((dr) => { const o = { ...dr }; const st = standard(d); if (st.every((x, n) => x === saved(d)[n])) delete o[d.key]; else o[d.key] = st.map(String); return o; });
  const apply = async () => {
    if (problems.length) return;
    setBusy(true);
    const next: Record<string, { v: number }[]> = {};
    for (const d of defs) {
      if (!isNum(d)) continue;
      const vals = (draft[d.key] ?? saved(d).map(String)).map(Number);
      if (vals.some((v, i) => v !== standard(d)[i])) next[d.key] = vals.map((v) => ({ v }));
    }
    if (await save({ rule_overrides: next }, "Saved. Ratings use the new numbers from now on.")) setDraft({});
    setBusy(false);
  };
  const changedIn = (a: string) => defs.filter((d) => d.areas.includes(a as never) && differs(d)).length;
  const list = defs.filter((d) => d.areas.includes(area as never));
  const anyChanged = defs.some(differs);
  const box = (d: MetricDef, i: number) => {
    const v = current(d)[i]!;
    const off = Number(v) !== standard(d)[i];
    return s.can_manage
      ? <input className={`inp inp-sm rd-num ${off ? "off" : ""}`} inputMode="decimal" aria-label={`${d.label}: ${d.rules[i]!.level === "red" ? "red" : "attention"} line`} value={v} onChange={(e) => set(d, i, e.target.value)} />
      : <b>{v}</b>;
  };
  const text = (d: MetricDef, part: string) => part.replace(/\{(\d)\}/g, (_, i: string) => current(d)[Number(i)] ?? "");

  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="reviewhub" />
        <h1 className="title">Rating definitions</h1>
        <div className="lede">What turns a number into a color on the scorecard. You can move the lines; the measures stay the same, so quarters compare fairly.</div>
      </div>
      {s.can_manage && anyChanged && <div className="row-btns"><button className="btn btn-ghost" onClick={() => setDraft(Object.fromEntries(defs.filter(isNum).map((d) => [d.key, standard(d).map(String)])))}>Reset all to the standard</button></div>}
      </div>
      <CompanyTabs codes={codes} co={co} pick={pick} />
      <SaveBar n={Object.keys(draft).length} busy={busy} onSave={() => void apply()} onUndo={() => setDraft({})} />
      {problems.length > 0 && <div className="note bad" style={{ marginBottom: 12 }}>Fix the highlighted numbers before saving.</div>}

      <div className="card rd-how">
        <div className="rd-step"><span className="rd-n">1</span><div><b>Each measure gets a color</b><span>from the lines below.</span>
          <div className="rd-key"><span className="rd-chip g">Healthy</span><span className="rd-chip y">Attention</span><span className="rd-chip r">Action required</span></div></div></div>
        <div className="rd-step"><span className="rd-n">2</span><div><b>An area takes its worst color</b><span>One red measure makes the whole area red.</span></div></div>
        <div className="rd-step"><span className="rd-n">3</span><div><b>Required measures can't be skipped</b><span>Leave one blank and the area shows Incomplete until it's filled in.</span></div></div>
      </div>

      <div className="rd-areas" role="tablist" aria-label="Areas">
        {AREAS.map((a) => {
          const n = defs.filter((d) => d.areas.includes(a.key)).length;
          const c = changedIn(a.key);
          return <button key={a.key} role="tab" aria-selected={a.key === area} className={a.key === area ? "on" : ""} onClick={() => setArea(a.key)}>
            {a.name}<span className="rd-cnt">{n}</span>{c > 0 && <span className="rd-chg" title={`${c} changed from the standard`}>{c} changed</span>}</button>;
        })}
      </div>

      <div className="rd-list">
        {list.map((d) => {
          const bands = bandsOf(d);
          const prob = draft[d.key] ? orderProblem(d, draft[d.key]!.map(Number)) : null;
          return (
            <div key={d.key} className={`card rd-row ${prob ? "bad" : ""}`}>
              <div className="rd-name">
                <b>{d.label}</b>
                <div className="rd-tags"><span className={`rd-req ${d.required ? "y" : ""}`}>{d.required ? "Required" : "Optional"}</span>{!isNum(d) && <span className="rd-req">Fixed</span>}{d.areas.length > 1 && <span className="rd-req">Also in {AREAS.filter((a) => a.key !== area && d.areas.includes(a.key)).map((a) => a.name).join(", ")}</span>}</div>
                {d.help && <div className="rd-help">{d.help}</div>}
              </div>
              <div className="rd-bands">
                {bands.map((b) => (
                  <div key={b.level} className={`rd-band ${b.level[0]}`}>
                    <span className="rd-lvl">{LEVEL_WORD[b.level]}</span>
                    <span className="rd-rng">{b.parts.map((p, k) => (typeof p === "string" ? <span key={k}>{text(d, p)}</span> : <span key={k}>{box(d, p.rule)}</span>))}</span>
                  </div>
                ))}
                {prob && <div className="rd-err">{prob}</div>}
                {!prob && differs(d) && <div className="rd-diff">Standard: {ruleText(d, d.rules).replace(/^Red /, "red ").replace(", Attention", ", attention")}.{s.can_manage && <> <button className="linkbtn" onClick={() => reset(d)}>Use the standard</button></>}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="foot-note">The standard is ITAssurance's Rating Definitions v1.0. Yes-or-no and choice measures (like DMARC) are fixed. When you save, the colors on every review follow the new lines; a page already given to a client keeps what it showed. Every change is in the audit log.</div>
    </div>
  );
}

// ---------------------------------------------------------------- Reminders and alerts
export function RemindersPage() {
  const { codes, co, pick } = useCompany();
  const { s, save } = useSettings(co);
  if (!codes.length) return <Navigate to="/settings" replace />;
  if (!s) return <Loading />;
  const off = new Set(s.settings.reminders_off);
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="reviewhub" />
        <h1 className="title">Reminders and alerts</h1>
        <div className="lede">The review cycle runs itself: every step reminds the right person in My tasks, and anything slipping is raised before the meeting, not after.</div>
      </div></div>
      <CompanyTabs codes={codes} co={co} pick={pick} />
      <div className="card tbl-wrap">
        <table className="tbl">
          <thead><tr><th>When</th><th>What happens</th><th>Who hears</th><th>How</th><th>On</th></tr></thead>
          <tbody>
            {REMINDERS.map((r) => (
              <tr key={r.key}>
                <td><b>{r.when}</b></td><td>{r.what}</td><td>{r.who}</td><td className="cap">{r.how}</td>
                <td><label className="switch"><input type="checkbox" checked={!off.has(r.key)} disabled={!s.can_manage}
                  onChange={(e) => void save({ reminders_off: e.target.checked ? [...off].filter((k) => k !== r.key) : [...off, r.key] })} aria-label={r.when} /><span /></label></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="foot-note">Days count from the booked meeting. Each email goes once, except the failing-gate alert, which can go up to 3 times. My tasks keeps reminding as usual (3 days, then weekly). Each person still chooses email or My tasks only, behind the bell in My tasks.</div>
    </div>
  );
}

// ---------------------------------------------------------------- Client page sections
export function ClientPageSectionsPage() {
  const { codes, co, pick } = useCompany();
  const { s, save } = useSettings(co);
  if (!codes.length) return <Navigate to="/settings" replace />;
  if (!s) return <Loading />;
  const list = s.settings.sections;
  const meta = (k: string) => s.sections.find((x) => x.key === k)!;
  const move = (i: number, d: -1 | 1) => { const n = [...list]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x!); void save({ sections: n }); };
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="reviewhub" />
        <h1 className="title">Client page</h1>
        <div className="lede">Which sections every review's client page has, and in what order. The design is your approved TBR page (the Eagle Systems review is the base). Editors can still hide a section on one review.</div>
      </div></div>
      <CompanyTabs codes={codes} co={co} pick={pick} />
      <div className="card tbl-wrap">
        <table className="tbl">
          <thead><tr><th>#</th><th>Section</th><th>Group</th><th>Filled from</th><th>On</th>{s.can_manage && <th>Order</th>}</tr></thead>
          <tbody>
            {list.map((x, i) => (
              <tr key={x.key}>
                <td className="num">{i + 1}</td><td><b>{meta(x.key).sub}</b></td><td className="cap">{meta(x.key).group}</td>
                <td className="cap">{meta(x.key).auto ? "The review" : "Wording per review"}</td>
                <td><label className="switch"><input type="checkbox" checked={x.on} disabled={!s.can_manage} onChange={(e) => void save({ sections: list.map((y) => (y.key === x.key ? { ...y, on: e.target.checked } : y)) })} aria-label={meta(x.key).sub} /><span /></label></td>
                {s.can_manage && <td className="acts"><button className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">Up</button><button className="btn btn-ghost btn-sm" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move down">Down</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="foot-note">The business pulse stays screen-free in the meeting; its section only lists the topics. Pulse notes never appear on the client page.</div>
    </div>
  );
}
